import { InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { fileRequest } from 'api/FileRequest';
import {
  ApiResponseGetBusinessPackages,
  BusinessPackage,
} from 'api/types/business/businessPackage';
import { businessPackageRequest } from '../../business/BusinessPackageRequest';
import { queryKeys } from '../../constants/queryKeys';
import { PaginationOptions } from './types';

function useGetBusinessFavorites({
  limit = 10,
  enabled = true,
}: PaginationOptions = {}) {
  const QUERY_KEY = [queryKeys.businessFavorites, { limit }];

  const selectData = (data: InfiniteData<ApiResponseGetBusinessPackages>) => ({
    pages: data.pages
      .flatMap((res) => res.results)
      .map((element: BusinessPackage) => ({
        ...element,
        presentationPhoto: fileRequest.getPublicUrl(element.presentationPhoto),
        presentationVideo: fileRequest.getPublicUrl(element.presentationVideo),
        company: {
          name: element.company.name,
          logo: fileRequest.getPublicUrl(element.company.presentationPhoto),
        },
        videoThumbnail: element.presentationVideo?.thumbnailPath
          ? fileRequest.getPublicUrl({
              ...element.presentationVideo,
              path: element.presentationVideo.thumbnailPath,
            })
          : undefined,
      })),
    pageParams: data.pageParams,
    total: data.pages.flatMap((res) => res.total)[0],
  });

  return useInfiniteQuery({
    queryKey: QUERY_KEY,
    queryFn: ({ pageParam }) =>
      businessPackageRequest.getBusinessFavorites(limit, pageParam as number),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      if (
        !lastPage?.results ||
        lastPage.results.length === 0 ||
        allPages.length * limit >= lastPage.total ||
        lastPage.total <= limit
      ) {
        return undefined;
      }
      return (lastPageParam as number) + limit;
    },
    select: selectData,
    enabled,
  });
}

export default useGetBusinessFavorites;
